import type { RefObject } from 'react'

import type { RobotMediaItem } from '../../../services/api/domains/robots.api'

export interface RobotMediaViewProps {
  item: RobotMediaItem
  /** Resolved presigned URL, or null while resolving. */
  url: string | null
  className?: string
  videoRef?: RefObject<HTMLVideoElement | null>
  onReady?: () => void
  onError?: () => void
}

/**
 * Render one capture, whatever kind it is.
 *
 * Deliberately ONE renderer shared by the inline preview and the fullscreen viewer. Two surfaces
 * each branching on `kind` independently is exactly how the audio arm gets forgotten and a voice
 * note ends up inside a `<video>` element that will never show anything.
 *
 * Playback is a plain media element on a presigned file. A static object over HTTP needs no Media
 * Source Extensions and no shared clock — the live-stream players in this app exist because a live
 * camera has neither of those properties.
 */
export function RobotMediaView({ item, url, className, videoRef, onReady, onError }: RobotMediaViewProps) {
  if (!url) {
    return <p className={`flex items-center justify-center text-sm text-white/70 ${className ?? ''}`}>Loading…</p>
  }

  if (item.kind === 'image') {
    return <img src={url} alt={item.deviceFile ?? 'Capture'} onLoad={onReady} onError={onError} className={className} />
  }

  if (item.kind === 'video') {
    return (
      <video
        ref={videoRef}
        data-testid="robot-media-video"
        src={url}
        controls
        autoPlay
        playsInline
        // `onLoadedData`, not `onLoad` — a media element never fires `load`, so an img-shaped
        // handler here would silently never re-arm the URL self-heal.
        onLoadedData={onReady}
        onError={onError}
        className={className}
      />
    )
  }

  return (
    <audio
      data-testid="robot-media-audio"
      src={url}
      controls
      autoPlay
      onLoadedData={onReady}
      onError={onError}
      className={className}
    />
  )
}
